import AccessTime from '@mui/icons-material/AccessTime'
import AddRounded from '@mui/icons-material/AddRounded'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import MedicationOutlined from '@mui/icons-material/MedicationOutlined'
import RemoveRounded from '@mui/icons-material/RemoveRounded'
import VideocamIcon from '@mui/icons-material/Videocam'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { Button, Box, Chip, IconButton, Tooltip } from '@mui/material'
import dayjs from 'dayjs'
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { RectangleRadioButton } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingComponents'
import { MOMENT_FORMATS, BOOKED_EVENT_MODES, displayNamesOfEventTypes } from 'src/consumer/constants'
import { getBookedSlotDetails } from 'src/mentor/services/bookedSlotCache'
import { usePromiseDialog } from 'src/customHooks/usePromiseDialog'
import DayjsDatePicker from 'src/mentor/components/commonReportComponents/components/datePicker/DayjsDatePicker'
import {
  calculateNextVisitDaysFromDateDayjs,
  calculateNextVistDateInMoment,
} from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageComponents'
import {
  getNextVisitDateDifference,
  getOffDayWeekdaysFromSchedules,
} from 'src/mentor/components/commonReportComponents/utils/nextVisitRosterCalendarUtils'
import {
  buildNextVisitSuggestionsFromDrugsAdvice,
  formatRxDurationSuggestionLabel,
} from 'src/mentor/components/commonReportComponents/utils/nextVisitRxDurationSuggestions'
import NextVisitSlotSelector from './NextVisitSlotSelector'

const NEXT_VISIT_MAX_BY_TYPE = { days: 365, weeks: 52, months: 12 }

const EMRNextVisitComponent = forwardRef(
  (
    {
      nextVisitInputType,
      setNextVisitInputType,
      nextVisitInputValue,
      setNextVisitInputValue,
      bookedEventInfoStartTime,
      nextVisitBookedEventId,
      showHoldSlotForNextVisit = false,
      currentClinicLocation,
      mentorProfileData,
      roomId,
      currentEventTypeId,
      currentPatient,
      refreshCurrentBookedEventInfoData,
      currentFee,
      currentCurrency,
      currentBookedEventId,
      saveReport,
      nextVisitHoldSlotOmissionReason,
      setNextVisitHoldSlotOmissionReason,
      isMarkingAsCompleted,
      leaveRoom,
      drugsAdvice,
    },
    componentRef,
  ) => {
    const [calendarVisible, setCalendarVisible] = useState(false)
    const [slotSelectorOpen, setSlotSelectorOpen] = useState(false)
    const [bookedSlot, setBookedSlot] = useState(null)
    const [loading, setLoading] = useState(false)
    const holdSlotOmissionDialog = usePromiseDialog()
    const promiseResolverRef = useRef(null)
    // Guards for the Rx-duration preset: once the practitioner has edited the field (or
    // it ever held a value from a saved draft / autofill), presets stop overriding it.
    const hasUserTouchedRef = useRef(false)
    const everHadValueRef = useRef(false)
    const lastPresetRef = useRef(null)

    const selectedDate = useMemo(() => {
      const calculatedDate = calculateNextVistDateInMoment(
        nextVisitInputValue, // Default to 7 days if no value
        nextVisitInputType,
        dayjs.utc(bookedEventInfoStartTime).local(),
      )

      return dayjs(calculatedDate)
    }, [nextVisitInputValue, nextVisitInputType, bookedEventInfoStartTime])

    // A next visit can only be in the future, so the input never shows anything but a
    // positive whole number (older saved data could carry negatives, which also made
    // the old input uneditable — every keystroke on "-83" failed validation).
    const numericInputValue = Number(nextVisitInputValue)
    const hasPositiveValue = Number.isFinite(numericInputValue) && numericInputValue > 0
    const displayInputValue = hasPositiveValue ? String(Math.round(numericInputValue)) : ''
    const maxInputValue = NEXT_VISIT_MAX_BY_TYPE[nextVisitInputType] || NEXT_VISIT_MAX_BY_TYPE.days

    // Self-heal any negative value that reaches this field (legacy drafts, old autofill)
    useEffect(() => {
      if (Number.isFinite(numericInputValue) && numericInputValue < 0) {
        setNextVisitInputValue('')
      }
    }, [numericInputValue, setNextVisitInputValue])

    useEffect(() => {
      if (hasPositiveValue) {
        everHadValueRef.current = true
      }
    }, [hasPositiveValue])

    // Suggested next-visit dates derived from the Rx Duration column. Longest course =
    // recommended default (review once the full treatment completes); shorter distinct
    // durations are offered as interim-review options.
    const rxDurationSuggestions = useMemo(
      () => buildNextVisitSuggestionsFromDrugsAdvice(drugsAdvice),
      [drugsAdvice],
    )

    // Preset the field from the Rx durations while the practitioner hasn't decided
    // anything themselves: it fills an untouched empty field and keeps following the
    // full course as durations change, but never overrides a saved / autofilled /
    // hand-entered value, and never refills a field that was deliberately cleared.
    useEffect(() => {
      if (hasUserTouchedRef.current || bookedSlot || nextVisitBookedEventId) return
      const fullCourse = rxDurationSuggestions[rxDurationSuggestions.length - 1]
      if (!fullCourse) return
      const currentValue = Number(nextVisitInputValue)
      const currentIsPositive = Number.isFinite(currentValue) && currentValue > 0
      const matchesLastPreset =
        lastPresetRef.current &&
        currentValue === lastPresetRef.current.value &&
        nextVisitInputType === lastPresetRef.current.unit
      if (currentIsPositive && !matchesLastPreset) return
      if (!currentIsPositive && everHadValueRef.current) return
      if (currentValue === fullCourse.value && nextVisitInputType === fullCourse.unit) {
        lastPresetRef.current = { value: fullCourse.value, unit: fullCourse.unit }
        return
      }
      setNextVisitInputType(fullCourse.unit)
      setNextVisitInputValue(fullCourse.value)
      lastPresetRef.current = { value: fullCourse.value, unit: fullCourse.unit }
    }, [
      rxDurationSuggestions,
      bookedSlot,
      nextVisitBookedEventId,
      nextVisitInputValue,
      nextVisitInputType,
      setNextVisitInputValue,
      setNextVisitInputType,
    ])

    function commitTypedNextVisitValue(rawText) {
      hasUserTouchedRef.current = true
      const digitsOnly = String(rawText ?? '').replace(/[^0-9]/g, '')
      const parsed = parseInt(digitsOnly, 10)
      if (!digitsOnly || !Number.isFinite(parsed) || parsed <= 0) {
        setNextVisitInputValue('')
        return
      }
      setNextVisitInputValue(Math.min(parsed, maxInputValue))
    }

    function stepNextVisitValue(delta) {
      hasUserTouchedRef.current = true
      const current = hasPositiveValue ? Math.round(numericInputValue) : 0
      const next = Math.min(Math.max(current + delta, 0), maxInputValue)
      setNextVisitInputValue(next > 0 ? next : '')
    }

    function applyRxDurationSuggestion(suggestion) {
      hasUserTouchedRef.current = true
      setNextVisitInputType(suggestion.unit)
      setNextVisitInputValue(suggestion.value)
      // With slot-holding enabled the pill leads straight into picking a time for that
      // date; otherwise it simply fills the value + unit above.
      if (showHoldSlotForNextVisit && !nextVisitBookedEventId) {
        setSlotSelectorOpen(true)
      }
    }
    
    // Remove console.log that was causing continuous logging
    // const currentEventTypeObject = useMemo(() => {
    //   mentorProfileData?.eventTypes?.find((eventType) => eventType?.uid === currentEventTypeId)
    // }, [mentorProfileData, currentEventTypeId])
    const currentEventTypeObject = useMemo(() => {
      return mentorProfileData?.eventTypes?.find(
        (eventType) => eventType?.uid === currentEventTypeId,
      )
    }, [mentorProfileData, currentEventTypeId])

    const showBookSpecificTimeButton =
      nextVisitInputValue > 0 && showHoldSlotForNextVisit && !nextVisitBookedEventId

    // Weekdays the practitioner doesn't work — painted red in the date picker (still selectable).
    const offDayWeekdays = useMemo(
      () => getOffDayWeekdaysFromSchedules(mentorProfileData, currentEventTypeId),
      [mentorProfileData, currentEventTypeId],
    )

    const todayDate = new Date()
    const maxDate = new Date()
    maxDate?.setMonth(todayDate?.getMonth() + 12)

    const dayjsMinDateInLocalTimezone = dayjs.utc(bookedEventInfoStartTime).local()
    const minDate = dayjsMinDateInLocalTimezone.toDate()

    minDate.setHours(0, 0, 0, 0)

    const handleSlotSelection = (slotData) => {
      setBookedSlot(slotData)
      // Calculate days from booked event start time to selected slot
      const selectedDateTime = dayjs(slotData.startDateTime || slotData.startTime)
      setSelectedNextVisitDate(selectedDateTime.startOf('day'))
      // // const differenceInDays = selectedDateTime.diff(eventStartTime, 'day')
      // const differenceInDays = calculateNextVisitDaysFromDateDayjs(
      //   selectedDateTime.format(MOMENT_FORMATS.formatThatServerSends),
      //   eventStartTime,
      // )

      // // Update the next visit values to reflect the exact booking
      // setNextVisitInputType('days')
      // setNextVisitInputValue(differenceInDays)
    }

    function resetNextVisit() {
      setBookedSlot(null)
      setNextVisitInputValue('')
      setNextVisitInputType('days')
    }

    function setSelectedNextVisitDate(nextValue) {
      const differenceInDays = calculateNextVisitDaysFromDateDayjs(
        dayjs(nextValue).format(MOMENT_FORMATS.formatThatServerSends),
        dayjs.utc(bookedEventInfoStartTime).local(),
      )

      hasUserTouchedRef.current = true
      setNextVisitInputType('days')
      setNextVisitInputValue(differenceInDays)
      if (showHoldSlotForNextVisit) {
        setSlotSelectorOpen(true)
      }
    }

    useEffect(() => {
      if (!nextVisitBookedEventId) return
      let cancelled = false
      setLoading(true)
      getBookedSlotDetails(nextVisitBookedEventId).then((slot) => {
        if (cancelled) return
        if (slot) setBookedSlot(slot)
        setLoading(false)
      })
      return () => {
        cancelled = true
      }
    }, [nextVisitBookedEventId])

    useImperativeHandle(componentRef, () => ({
      /**
       * Opens the hold slot omission reason dialog and returns a promise
       * @returns {Promise} Resolves when user provides reason, rejects if cancelled
       */
      confirmOmissionReason: () => {
        return holdSlotOmissionDialog.show()
      },
    }))

    return (
      <>
        <div>
          {/* Show booked slot info if available */}
          {bookedSlot && !loading && (
            <div
              style={{
                marginBottom: '16px',
                padding: '16px',
                backgroundColor: bookedSlot?.status === 'booked' ? '#e8f5e8' : '#fff8e1',
                borderRadius: '8px',
                border: `1.5px solid ${bookedSlot?.status === 'booked' ? '#4caf50' : '#ffc107'}`,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Header with Status */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: bookedSlot?.status === 'booked' ? '#2e7d32' : '#ff8f00',
                        marginBottom: '8px',
                      }}
                    >
                      {bookedSlot?.status === 'booked' ? '✅' : '⏳'} Next Visit Slot{' '}
                      {bookedSlot?.status === 'booked' ? 'Booked' : 'Blocked'}
                    </div>
                    
                    {/* Time Slot */}
                    <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px', color: '#333' }}>
                      {bookedSlot.timeSlot || (() => {
                        try {
                          const start = dayjs(bookedSlot.startTime)
                          const end = bookedSlot.endTime ? dayjs(bookedSlot.endTime) : start.add(30, 'minutes')
                          return `${start.format('h:mm A')} - ${end.format('h:mm A')}`
                        } catch {
                          return 'Time not available'
                        }
                      })()}
                    </div>
                    
                    {/* Date and Relative Time */}
                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                      {dayjs(bookedSlot.startTime).format('dddd, MMMM D, YYYY')} (
                      {getNextVisitDateDifference(dayjs(bookedSlot.startTime), dayjs(minDate))})
                    </div>
                  </div>
                </div>

                {/* Details Section */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    paddingTop: '8px',
                    borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                  }}
                >
                  {/* Event Type */}
                  {bookedSlot?.eventType && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                      <CalendarMonthIcon style={{ fontSize: '16px', color: '#666' }} />
                      <span style={{ color: '#666' }}>Event Type:</span>
                      <span style={{ fontWeight: '600', color: '#333' }}>
                        {displayNamesOfEventTypes?.[bookedSlot.eventType] || bookedSlot.eventType}
                      </span>
                    </div>
                  )}

                  {/* Meeting Mode (Online/Offline) */}
                  {bookedSlot?.meetingSrc && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                      {bookedSlot.meetingSrc === 'aumhum' || bookedSlot.meetingSrc === 'online' ? (
                        <VideocamIcon style={{ fontSize: '16px', color: '#1976d2' }} />
                      ) : (
                        <LocationOnIcon style={{ fontSize: '16px', color: '#d32f2f' }} />
                      )}
                      <span style={{ color: '#666' }}>Mode:</span>
                      <span style={{ fontWeight: '600', color: '#333' }}>
                        {BOOKED_EVENT_MODES?.[bookedSlot.meetingSrc]?.title || 
                         (bookedSlot.meetingSrc === 'aumhum' || bookedSlot.meetingSrc === 'online' ? 'Online' : 'Offline')}
                      </span>
                    </div>
                  )}

                  {/* Chamber Room (for offline sessions) */}
                  {bookedSlot?.chamberRoomName && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                      <LocationOnIcon style={{ fontSize: '16px', color: '#d32f2f' }} />
                      <span style={{ color: '#666' }}>Location:</span>
                      <span style={{ fontWeight: '600', color: '#333' }}>
                        {bookedSlot.chamberRoomName}
                      </span>
                    </div>
                  )}

                  {/* Duration */}
                  {bookedSlot?.startTime && bookedSlot?.endTime && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                      <AccessTime style={{ fontSize: '16px', color: '#666' }} />
                      <span style={{ color: '#666' }}>Duration:</span>
                      <span style={{ fontWeight: '600', color: '#333' }}>
                        {(() => {
                          try {
                            const start = dayjs(bookedSlot.startTime)
                            const end = dayjs(bookedSlot.endTime)
                            const durationMinutes = end.diff(start, 'minute')
                            if (durationMinutes < 60) {
                              return `${durationMinutes} minutes`
                            }
                            const hours = Math.floor(durationMinutes / 60)
                            const minutes = durationMinutes % 60
                            return minutes > 0 ? `${hours}h ${minutes}m` : `${hours} hour${hours > 1 ? 's' : ''}`
                          } catch {
                            return 'N/A'
                          }
                        })()}
                      </span>
                    </div>
                  )}

                  {/* Fee Information (if available) */}
                  {bookedSlot?.feePaid && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                      <span style={{ color: '#666' }}>Fee:</span>
                      <span style={{ fontWeight: '600', color: '#333' }}>
                        {bookedSlot.currency?.toUpperCase() || 'INR'} {bookedSlot.feePaid}
                      </span>
                    </div>
                  )}
                </div>

                {/* Action Buttons Section - Reserved for future actions */}
                {/* 
                <div style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  marginTop: '12px',
                  paddingTop: '12px',
                  borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      // TODO: Handle modify action
                    }}
                  >
                    Modify
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={() => {
                      // TODO: Handle cancel action
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    onClick={() => {
                      // TODO: Handle change event type action
                    }}
                  >
                    Change Mode
                  </Button>
                </div>
                */}
              </div>
            </div>
          )}
          {!(bookedSlot && !loading) && (
            <>
              {/* wraps so the phone / narrow split-panel layout stacks instead of overflowing */}
              <Box sx={{ display: 'flex', gap: '12px 16px', flexWrap: 'wrap', alignItems: 'center' }}>
                {/* − / value / + stepper: free numeric typing (digits only, clamped to a
                    sane max) plus one-tap adjustment — much easier than the old number
                    input, especially on touch screens. Only positive values can exist. */}
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'stretch',
                    flexShrink: 0,
                    border: '1px solid #b4b7ba',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    background: '#fff',
                    '&:focus-within': {
                      borderColor: '#7450aa',
                      boxShadow: '0 0 0 2px rgba(116, 80, 170, 0.15)',
                    },
                  }}
                >
                  <IconButton
                    aria-label="Decrease next visit value"
                    disabled={!!bookedSlot || !hasPositiveValue}
                    onClick={() => stepNextVisitValue(-1)}
                    size="small"
                    sx={{ borderRadius: 0, px: 1, color: '#4338ca' }}
                  >
                    <RemoveRounded sx={{ fontSize: 18 }} />
                  </IconButton>
                  <Box
                    component="input"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Number"
                    aria-label="Next visit after"
                    disabled={!!bookedSlot}
                    value={displayInputValue}
                    onChange={(e) => commitTypedNextVisitValue(e.target.value)}
                    sx={{
                      width: '68px',
                      border: 'none',
                      outline: 'none',
                      textAlign: 'center',
                      fontWeight: 600,
                      fontSize: '16px',
                      color: '#1e293b',
                      background: 'transparent',
                      padding: '8px 2px',
                      minWidth: 0,
                      '&::placeholder': { color: '#b6bcc8', fontWeight: 400, fontSize: '13px' },
                    }}
                  />
                  <IconButton
                    aria-label="Increase next visit value"
                    disabled={!!bookedSlot}
                    onClick={() => stepNextVisitValue(1)}
                    size="small"
                    sx={{ borderRadius: 0, px: 1, color: '#4338ca' }}
                  >
                    <AddRounded sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
                <Box sx={{ width: '200px', flexShrink: 0 }}>
                  <RectangleRadioButton
                    disabled={!!bookedSlot}
                    options={[
                      { label: 'Days', id: 'days' },
                      { label: 'Weeks', id: 'weeks' },
                      { label: 'Months', id: 'months' },
                    ]}
                    selectedId={nextVisitInputType}
                    selectionFunction={(id) => {
                      hasUserTouchedRef.current = true
                      setNextVisitInputType(id)
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  <Box component="span" sx={{ color: '#666', fontSize: '14px' }}>or</Box>
                  <DayjsDatePicker
                    date={calculateNextVistDateInMoment(
                      nextVisitInputValue,
                      nextVisitInputType,
                      dayjs.utc(bookedEventInfoStartTime),
                    ).local()}
                    offDayWeekdays={offDayWeekdays}
                    calendarProps={{
                      minDate: dayjs().toDate(),
                      maxDate: maxDate,
                      minDetail: 'decade',
                      showNeighbouringMonth: false,
                      locale: sessionStorage.getItem('languageCode') || 'en',
                    }}
                    format="DD MMM YYYY, dddd"
                    buttonProps={{
                      variant: 'outlined',
                      size: 'small',
                      disabled: !!bookedSlot,
                      startIcon: <CalendarMonthIcon />,
                      sx: {
                        background: '#fff',
                        textTransform: 'none',
                        border: '1px solid #cccccc',
                      },
                      // disableRipple: true,
                    }}
                    extraHandlingAfterOnClick={() => {
                      if (
                        (nextVisitInputValue === null || nextVisitInputValue === undefined) &&
                        !calendarVisible
                      ) {
                        hasUserTouchedRef.current = true
                        setNextVisitInputValue(7)
                      }
                    }}
                    onChange={setSelectedNextVisitDate}
                    placeholder="Choose Next Visit Date"
                  />
                </Box>
              </Box>

              {/* Suggested dates derived from the Rx Duration column — one pill per
                  distinct medicine end point, longest course highlighted. Clicking a
                  pill fills the value/unit above (and, when slot-holding is enabled,
                  goes straight into picking a time for that date). */}
              {rxDurationSuggestions?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mb: 0.75,
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#64748b',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    <MedicationOutlined sx={{ fontSize: 14, color: '#4338ca' }} />
                    Suggested from Rx durations
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {rxDurationSuggestions.map((suggestion) => {
                      const suggestionDate = calculateNextVistDateInMoment(
                        suggestion.value,
                        suggestion.unit,
                        dayjs.utc(bookedEventInfoStartTime),
                      ).local()
                      const isSelected =
                        Number(nextVisitInputValue) === suggestion.value &&
                        nextVisitInputType === suggestion.unit
                      const drugNamesText = suggestion.drugNames?.join(', ')
                      const tooltipText = `${
                        suggestion.isFullCourse
                          ? `Full course ends — ${drugNamesText}`
                          : `${drugNamesText} ${
                              suggestion.drugNames?.length > 1 ? 'finish' : 'finishes'
                            } here`
                      }${showHoldSlotForNextVisit ? '. Tap to pick a time slot' : ''}`
                      return (
                        <Tooltip key={suggestion.days} title={tooltipText} arrow>
                          <Chip
                            label={`${formatRxDurationSuggestionLabel(suggestion)} · ${suggestionDate.format(
                              'D MMM',
                            )}${suggestion.isFullCourse ? ' · course ends' : ''}`}
                            onClick={() => applyRxDurationSuggestion(suggestion)}
                            clickable
                            size="small"
                            variant={isSelected ? 'filled' : 'outlined'}
                            sx={{
                              fontWeight: 600,
                              fontSize: '12px',
                              height: '28px',
                              borderColor: 'rgba(67, 56, 202, 0.35)',
                              color: isSelected ? '#fff' : '#4338ca',
                              backgroundColor: isSelected ? '#4338ca' : 'rgba(67, 56, 202, 0.04)',
                              '&:hover': {
                                backgroundColor: isSelected ? '#3730a3' : 'rgba(67, 56, 202, 0.12)',
                              },
                              '&.MuiChip-clickable:hover': {
                                backgroundColor: isSelected ? '#3730a3' : 'rgba(67, 56, 202, 0.12)',
                              },
                            }}
                          />
                        </Tooltip>
                      )
                    })}
                  </Box>
                </Box>
              )}

              {/* Book Specific Time Button */}
              {showBookSpecificTimeButton && !bookedSlot && (
                <div style={{ marginTop: '16px' }}>
                  <Button
                    variant="outlined"
                    background="emrPrimary"
                    startIcon={<AccessTime />}
                    onClick={() => setSlotSelectorOpen(true)}
                    style={{
                      textTransform: 'none',
                      backgroundColor: bookedSlot ? '#e3f2fd' : '#fff',
                    }}
                  >
                    {bookedSlot ? 'Change Specific Time' : 'Hold Slot For Next Visit'}
                  </Button>

                  {!bookedSlot && nextVisitInputValue && nextVisitInputType && (
                    <div
                      style={{
                        fontSize: '13px',
                        color: '#666',
                        marginTop: '8px',
                        fontStyle: 'italic',
                      }}
                    >
                      💡 Tip: Hold a slot for a specific time slot for the date:{' '}
                      {calculateNextVistDateInMoment(
                        nextVisitInputValue,
                        nextVisitInputType,
                        dayjs.utc(bookedEventInfoStartTime),
                      )
                        .local()
                        .format('DD/MM/YYYY, dddd')}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Slot Selector Modal */}
          <NextVisitSlotSelector
            open={slotSelectorOpen}
            setOpen={setSlotSelectorOpen}
            leaveRoom={leaveRoom}
            bookingDate={dayjsMinDateInLocalTimezone}
            onClose={(willNotResetNextVisit = false) => {
              setSlotSelectorOpen(false)
              if (!willNotResetNextVisit) {
                resetNextVisit()
              }
            }}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedNextVisitDate}
            onSlotSelect={handleSlotSelection}
            selectedItem={currentClinicLocation}
            roomId={roomId}
            mentorProfile={mentorProfileData}
            currentEventTypeObject={currentEventTypeObject}
            currentFee={currentFee}
            selectedPatient={currentPatient}
            refreshData={refreshCurrentBookedEventInfoData}
            currentBookedEventId={currentBookedEventId}
            currentCurrency={currentCurrency}
            saveReport={saveReport}
            nextVisitHoldSlotOmissionReason={nextVisitHoldSlotOmissionReason}
            setNextVisitHoldSlotOmissionReason={setNextVisitHoldSlotOmissionReason}
            isMarkingAsCompleted={isMarkingAsCompleted}
            holdSlotOmissionDialog={holdSlotOmissionDialog}
          />
        </div>
      </>
    )
  },
)

export default EMRNextVisitComponent
